// Oracle CREATE INDEX residual slot recognizers for the EditorContext
// projection. PRD: GRA-8 (cross-engine projection pivot), GRA8-ROLL-6.
// G11: pure text scan, NO web-tree-sitter — this is the legal projection-side
// body the future tree-sitter swap replaces behind the identical signature.
//
// GRA8-ROLL-6 adds the one residual Oracle CREATE INDEX slot the DDL family
// does NOT cover:
//   - `isOracleIndexExpressionSlot` — `CREATE [UNIQUE|BITMAP] INDEX … ON <tbl>
//     ( … <fn>(▮ … )`: a function-wrapped index expression (e.g.
//     `UPPER(▮email)`). The family's ddl-index-column recognizer matches the
//     index `(` directly preceded by `ON <tbl>`, but here the LAST unclosed `(`
//     is the FUNCTION paren, so it sees `… ON EMPLOYEES (UPPER` at its
//     tail and returns null → the cursor would reach the `unknown` arm where
//     the resolver (no FROM/alias bindings on a CREATE INDEX) leaks tables.
//     Maps to `'ddl-index-column'` — the same kind the resolver already routes
//     for the direct index column list — so `findOracleIndexTargetTable` +
//     `emitOracleTargetTableColumns` emit the index target's columns.
//     Pinned by oracle script.31 §4.
//
// Guard against double-classification: when the cursor's INNERMOST unclosed `(`
// IS the index `(` directly (no enclosing function paren), the recognizer
// returns false — that case is already owned by `classifyDdlSlot`'s
// ddl-index-column path. Only the function-wrapped residual goes through this
// recognizer.
//
// `resolveSlotKind` dispatches `isOracleIndexExpressionSlot` only on the
// `engine === 'oracle'` arm. `slotKindToCpk` already maps `ddl-index-column`.

import type { ClauseToken } from './editorContextClause';
import { findEnclosingLparenIndex, upper } from './editorContextSlotShared';

// Index-type prefixes Oracle accepts between CREATE and INDEX (`CREATE UNIQUE
// INDEX`, `CREATE BITMAP INDEX`). `upper()` normalises keyword/identifier kinds.
const INDEX_TYPE_NOISE: ReadonlySet<string> = new Set(['UNIQUE', 'BITMAP']);

/**
 * True when the cursor sits inside one OR MORE unclosed `(` whose OUTERMOST
 * relevant opener is the `CREATE [UNIQUE|BITMAP] INDEX … ON <tbl> (` column-list
 * paren, AND at least one inner function paren stands between the cursor and
 * that index `(`. The slot is a function-wrapped index expression
 * (`UPPER(▮email)`, `LOWER(TRIM(▮…))`).
 *
 * Walks OUT from the cursor: find the innermost enclosing `(`; if it is the
 * index `(` directly (preceded by `ON <tbl>`), return false (owned by
 * `classifyDdlSlot` — no double-classify). Otherwise pop that paren
 * (slice the token prefix up to and including the opener) and re-scan, repeating
 * until either the index `(` opener is reached (→ true) or no enclosing paren
 * remains (→ false). The statement head must be `CREATE [UNIQUE|BITMAP] INDEX`.
 */
export function isOracleIndexExpressionSlot(
  tokens: readonly ClauseToken[],
): boolean {
  // The cursor must sit inside at least one unclosed `(`.
  const innermost = findEnclosingLparenIndex(tokens);
  if (innermost < 0) return false;

  // The innermost paren must NOT itself be the index column-list opener — that
  // direct case is owned by classifyDdlSlot.
  if (precededByOnTable(tokens, innermost)) return false;

  // The statement must be a CREATE [UNIQUE|BITMAP] INDEX.
  if (!headIsCreateIndex(tokens)) return false;

  // Walk OUT past the (function) paren(s): pop the innermost opener and re-scan
  // the prefix before it. The slot fires once an enclosing opener is the index
  // `(` preceded by `ON <tbl>`.
  let lparenIdx = innermost;
  // Bound the walk by the number of tokens (each step strictly shrinks).
  for (let guard = tokens.length; guard >= 0; guard -= 1) {
    const prefix = tokens.slice(0, lparenIdx);
    const outer = findEnclosingLparenIndex(prefix);
    if (outer < 0) return false;
    if (precededByOnTable(prefix, outer)) return true;
    lparenIdx = outer;
  }
  return false;
}

/**
 * True when the unclosed `(` at `lparenIdx` is immediately preceded by an
 * `ON <table>` target (bare `ON tbl (` or schema-qualified `ON schema.tbl (`).
 * Mirrors the mysql `precededByOnTable` shape (it is `editorContextSlot.mysql`
 * -local, so re-expressed here rather than imported across recognizer files).
 */
function precededByOnTable(
  tokens: readonly ClauseToken[],
  lparenIdx: number,
): boolean {
  let i = lparenIdx - 1;
  if (i < 0 || tokens[i]!.kind !== 'identifier') return false;
  i -= 1;
  if (i >= 0 && tokens[i]!.kind === 'dot') {
    i -= 1;
    if (i < 0 || tokens[i]!.kind !== 'identifier') return false;
    i -= 1;
  }
  return upper(tokens[i]) === 'ON';
}

/**
 * Scan the head tokens for a `CREATE [UNIQUE|BITMAP] INDEX` lead. Walking
 * forward from each `CREATE`, the next non-noise keyword (tolerating UNIQUE /
 * BITMAP) must be `INDEX`. Returns true when any such lead precedes the cursor.
 */
function headIsCreateIndex(tokens: readonly ClauseToken[]): boolean {
  for (let i = 0; i < tokens.length; i += 1) {
    if (upper(tokens[i]) !== 'CREATE') continue;
    let j = i + 1;
    while (j < tokens.length && INDEX_TYPE_NOISE.has(upper(tokens[j]) ?? '')) j += 1;
    if (upper(tokens[j]) === 'INDEX') return true;
  }
  return false;
}
